import * as _ from 'lodash'
import * as moment from 'moment'
import { Address } from './address'
import { MappableModel } from './mappable-model'
import { Mapper } from '../services/mapper.service'
import { Membership } from './membership'
import { PersonaEmailAddress } from './persona-email-address'
import { PersonaMemberships } from './persona-memberships'
import { PhoneNumber } from './phone-number'
import { ProfileImage } from './profile-image'
import { User } from './user'
import { Ethnicity } from './ethnicity'
import { ElectronicDocumentAcceptance } from './electronic-document-acceptance'

export class Persona extends MappableModel {
  public address: Address
  public addresses: Address[]
  public date_of_birth: Date
  public graduation_year: Date
  public ethnicities: Ethnicity[]
  public disability: string
  public military_status: string
  public email_addresses: PersonaEmailAddress[]
  public first_name: string
  public gender: string
  public id: number
  public last_name: string
  public memberships: Membership[]
  public middle_name: string
  public nickname: string
  public persona_listenee: boolean
  public phone_numbers: PhoneNumber[]
  public profile_images: ProfileImage[]
  public suffix: string
  public type: string
  public uri: string
  public user: User
  public acceptances: ElectronicDocumentAcceptance[]
  private _personaMemberships: PersonaMemberships

  private _primaryAddress: Address
  private _primaryEmail: PersonaEmailAddress

  public get hasProfileImage(): boolean {
    return this.profile_images.length > 0
  }

  public profileImage(size: string): string {
    const img: ProfileImage = _.find(this.profile_images, ['image_type', size])
    return (img && img.url) || ''
  }

  public get isSelf(): boolean {
    return this.type === 'self'
  }

  public get isOther(): boolean {
    return this.type === 'other'
  }

  public get isUnderMinPurchaseAge(): boolean {
    return moment(this.date_of_birth).isAfter(moment().subtract(18, 'years'))
  }

  public get fullName(): string {
    return [this.first_name, this.last_name, this.suffix]
      .filter((n) => !!n)
      .join(' ').trim()
  }

  public get formattedGender(): string {
    if (this.gender === 'male') return 'Male'
    if (this.gender === 'female') return 'Female'
    if (this.gender === 'non-binary') return 'Non-binary'
    return null
  }

  public get primaryAddress(): Address {
    if (!this._primaryAddress) {
      if (this.addresses.length === 0) this.addresses.push(new Address({ }))
      this._primaryAddress = this.addresses[0]
    }
    return this._primaryAddress
  }

  public set primaryAddress(addr: Address) {
    this.addresses[0] = addr
  }

  public set primaryAddressData(data: any) {
    if (!this.primaryAddress) this.primaryAddress = new Address({ })
    this.primaryAddress.update(data)
  }

  public get primaryPhone(): string {
    return this.phone_numbers.length > 0 ? this.phone_numbers[0].number : ''
  }

  public set primaryPhone(phone: string) {
    if (this.phone_numbers.length > 0) this.phone_numbers[0].number = phone
    else this.phone_numbers.push(new PhoneNumber({ number: phone}))
  }

  public get primaryEmail(): PersonaEmailAddress {
    if (!this._primaryEmail) {
      if (this.email_addresses.length <= 0) {
        this.email_addresses.push(new PersonaEmailAddress({ }))
      }
      this._primaryEmail = this.email_addresses[0]
    }
    return this._primaryEmail
  }

  public set primaryEmail(email: PersonaEmailAddress) {
    _.each(this.email_addresses, (addr: PersonaEmailAddress) => {
      addr.is_primary = false
    })
    email.is_primary = true
    this.primaryEmail.update(email)
  }

  public get primaryEmailString(): string {
    return this.primaryEmail.address || ''
  }

  public set primaryEmailString(email: string) {
    if (this.primaryEmail) {
      this.primaryEmail.update({ address: email})
      return
    }
    this.primaryEmail = new PersonaEmailAddress({ address: email})
  }

  /**
   * does this persona have required attributes for purchasing a membership?
   */
  public get validForMembership(): boolean {
    if (!this.first_name) return false
    if (!this.last_name) return false
    if (!this.gender) return false
    if (!this.date_of_birth) return false

    return true
  }

  /**
   * is this person between 13-17 (inclusive) and have a grad year within 4 years of the most recent Sept 1st
   */
  public get isNCSAEligible(): boolean {
    if (!this.date_of_birth || !this.graduation_year) return false

    const age = moment().diff(this.date_of_birth, 'years')
    const gradYear = this.graduation_year.getFullYear()
    // momentjs months are zero indexed
    const currentSchoolYear = moment().month() < 8 ? moment() : moment().add(1, 'years')
    const latestGradYear = currentSchoolYear.add(4, 'years').toDate().getFullYear()
    return (age > 12 && age < 18) && (gradYear <= latestGradYear)
  }

  public deserialize() {
    this.addresses = this.address ? Mapper.asArray<Address>([this.address], Address) : Mapper.asArray<Address>(this.addresses, Address)
    this.email_addresses = Mapper.asArray<PersonaEmailAddress>(this.email_addresses, PersonaEmailAddress)
    this.phone_numbers = Mapper.asArray<PhoneNumber>(this.phone_numbers, PhoneNumber)
    this.profile_images = Mapper.asArray<ProfileImage>(this.profile_images, ProfileImage)
    this.ethnicities = Mapper.asArray<Ethnicity>(this.ethnicities, Ethnicity)
    if (this.date_of_birth && !(this.date_of_birth instanceof Date)) {
      this.date_of_birth = moment(this.date_of_birth).toDate()
    }
    if (this.graduation_year && !(this.graduation_year instanceof Date)) {
      const yearToString = this.graduation_year + ''
      // Day added so timezone conversion doesn't alter the given year
      this.graduation_year = moment(yearToString, 'YYYY').add(1, 'day').toDate()
    }
  }

  public get personaMemberships(): PersonaMemberships {
    if (!this._personaMemberships) {
      this._personaMemberships = new PersonaMemberships(this)
    }
    return this._personaMemberships
  }
}
